import React, { useState, useEffect } from 'react';
import { COVER_BG_IMAGE } from '../constants/cover';
import { matchesAPI } from '../services/api';
import { getLogoUrl } from '../utils/logoUrl';
import { FaCalendarAlt, FaDownload, FaClock, FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { useLanguage } from '../context/LanguageContext';

const CalendarPage = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedRound, setSelectedRound] = useState('all');
  const [selectedLeg, setSelectedLeg] = useState('all');
  const [showPDF, setShowPDF] = useState(false);
  const [activePdfUrl, setActivePdfUrl] = useState(null);

  const { language } = useLanguage();

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const response = await matchesAPI.getAll();
      setMatches(response.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Extraire la liste unique des équipes
  const allTeams = [];
  matches.forEach(m => {
    if (m.home_team_name && !allTeams.find(t => t.name === m.home_team_name)) {
      allTeams.push({ id: m.home_team_id, name: m.home_team_name });
    }
    if (m.away_team_name && !allTeams.find(t => t.name === m.away_team_name)) {
      allTeams.push({ id: m.away_team_id, name: m.away_team_name });
    }
  });
  allTeams.sort((a, b) => a.name.localeCompare(b.name));

  // Extraire les mois disponibles
  const availableMonths = [...new Set(matches.map(m => {
    const date = new Date(m.match_date);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }))].sort();

  // Extraire les rounds disponibles
  const availableRounds = [...new Set(matches.map(m => m.round).filter(r => r !== null && r !== undefined))].sort((a, b) => parseInt(a) - parseInt(b));

  // Filtrer par groupe, équipe et mois
  const filteredMatches = matches.filter(m => {
    // Filtre par groupe
    if (selectedGroup !== 'all') {
      const selectedG = parseInt(selectedGroup);
      const groupMatch = m.home_team_group === selectedG || m.away_team_group === selectedG;
      if (!groupMatch) return false;
    }

    // Filtre par équipe
    if (selectedTeam !== 'all') {
      const teamId = parseInt(selectedTeam);
      const teamMatch = m.home_team_id === teamId || m.away_team_id === teamId;
      if (!teamMatch) return false;
    }

    // Filtre par mois
    if (selectedMonth !== 'all') {
      const matchDate = new Date(m.match_date);
      const matchMonth = `${matchDate.getFullYear()}-${String(matchDate.getMonth() + 1).padStart(2, '0')}`;
      if (matchMonth !== selectedMonth) return false;
    }

    // Filtre par round
    if (selectedRound !== 'all') {
      if (m.round && String(m.round) !== String(selectedRound)) return false;
    }

    // Filtre par aller/retour
    if (selectedLeg !== 'all') {
      if (m.leg !== selectedLeg) return false;
    }

    return true;
  });

  // Grouper par date
  const matchesByDate = filteredMatches.reduce((acc, match) => {
    const date = new Date(match.match_date).toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(match);
    return acc;
  }, {});

  const sortedDates = Object.keys(matchesByDate).sort((a, b) => {
    // On retrouve un match pour comparer les dates réelles
    const dateA = new Date(matchesByDate[a][0].match_date);
    const dateB = new Date(matchesByDate[b][0].match_date);
    return dateA - dateB;
  });

  const groups = [
    { id: 1, name: 'Groupe 1', nameAr: 'المجموعة 1', file: '/documents/calendrier/Poule 1.pdf' },
    { id: 2, name: 'Groupe 2', nameAr: 'المجموعة 2', file: '/documents/calendrier/poule 2.pdf' },
    { id: 3, name: 'Groupe 3', nameAr: 'المجموعة 3', file: '/documents/calendrier/poule 3.pdf' },
    { id: 4, name: 'Groupe 4', nameAr: 'المجموعة 4', file: '/documents/calendrier/poule 4.pdf' },
    { id: 5, name: 'Groupe 5', nameAr: 'المجموعة 5', file: '/documents/calendrier/Poule 5.pdf' },
    { id: 6, name: 'Groupe 6', nameAr: 'المجموعة 6', file: '/documents/calendrier/Poule 6.pdf' }
  ];

  const getStatusBadge = (match) => {
    if (match.status === 'finished') {
      return { text: language === 'ar' ? 'انتهى' : 'Terminé', color: '#28a745' };
    } else if (match.status === 'live') {
      return { text: language === 'ar' ? '🔴 مباشر' : '🔴 En direct', color: '#dc3545' };
    } else {
      return { text: language === 'ar' ? 'قادم' : 'À venir', color: '#ffc107' };
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <section style={styles.header}>
        <FaCalendarAlt style={styles.headerIcon} />
        <h1 style={styles.title}>
          {language === 'ar' ? 'رزنامة المباريات' : 'Calendrier des Matchs'}
        </h1>
        <p style={styles.subtitle}>
          {language === 'ar' ? 'جميع مباريات الموسم 2025/2026' : 'Tous les matchs de la saison 2025/2026'}
        </p>
      </section>

      {/* Filters Section */}
      <section style={styles.filterSection}>
        <div style={styles.filterContainer}>
          <h2 style={styles.filterTitle}>
            {language === 'ar' ? 'تصفية المباريات' : 'Filtrer les matchs'}
          </h2>

          {/* Group Filter */}
          <div style={styles.filterRow}>
            <label style={styles.filterLabel}>
              {language === 'ar' ? 'المجموعة:' : 'Groupe:'}
            </label>
            <div style={styles.groupButtons}>
              <button
                onClick={() => setSelectedGroup('all')}
                style={{
                  ...styles.groupButton,
                  ...(selectedGroup === 'all' ? styles.groupButtonActive : {})
                }}
              >
                {language === 'ar' ? 'الكل' : 'Tous'}
              </button>
              {groups.map(group => (
                <button
                  key={group.id}
                  onClick={() => setSelectedGroup(group.id.toString())}
                  style={{
                    ...styles.groupButton,
                    ...(selectedGroup === group.id.toString() ? styles.groupButtonActive : {})
                  }}
                >
                  {language === 'ar' ? group.nameAr : group.name}
                </button>
              ))}
            </div>
          </div>

          {/* Select Filters Row */}
          <div style={styles.filtersGrid}>
            {/* Team Filter */}
            <div style={styles.filterItem}>
              <label style={styles.filterLabel}>
                {language === 'ar' ? 'الفريق:' : 'Équipe:'}
              </label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                style={styles.selectFilter}
              >
                <option value="all">{language === 'ar' ? 'جميع الفرق' : 'Toutes les équipes'}</option>
                {allTeams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>

            {/* Date Filter */}
            <div style={styles.filterItem}>
              <label style={styles.filterLabel}>
                {language === 'ar' ? 'الشهر:' : 'Mois:'}
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={styles.selectFilter}
              >
                <option value="all">{language === 'ar' ? 'جميع الأشهر' : 'Tous les mois'}</option>
                {availableMonths.map(month => {
                  const [year, monthNum] = month.split('-');
                  const monthName = new Date(year, parseInt(monthNum) - 1).toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR', { month: 'long', year: 'numeric' });
                  return (
                    <option key={month} value={month}>{monthName}</option>
                  );
                })}
              </select>
            </div>

            {/* Round Filter */}
            <div style={styles.filterItem}>
              <label style={styles.filterLabel}>
                {language === 'ar' ? 'الجولة:' : 'Journée:'}
              </label>
              <select
                value={selectedRound}
                onChange={(e) => setSelectedRound(e.target.value)}
                style={styles.selectFilter}
              >
                <option value="all">{language === 'ar' ? 'جميع الجولات' : 'Toutes les journées'}</option>
                {availableRounds.map(round => (
                  <option key={round} value={round}>
                    {language === 'ar' ? `الجولة ${round}` : `Journée ${round}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Leg Filter */}
            <div style={styles.filterItem}>
              <label style={styles.filterLabel}>
                {language === 'ar' ? 'الذهاب/الإياب:' : 'Aller/Retour:'}
              </label>
              <select
                value={selectedLeg}
                onChange={(e) => setSelectedLeg(e.target.value)}
                style={styles.selectFilter}
              >
                <option value="all">{language === 'ar' ? 'الكل' : 'Tous'}</option>
                <option value="aller">{language === 'ar' ? 'ذهاب' : 'Aller'}</option>
                <option value="retour">{language === 'ar' ? 'إياب' : 'Retour'}</option>
              </select>
            </div>
          </div>

          {/* Reset Filters Button */}
          {(selectedGroup !== 'all' || selectedTeam !== 'all' || selectedMonth !== 'all' || selectedRound !== 'all' || selectedLeg !== 'all') && (
            <div style={styles.resetButtonContainer}>
              <button
                onClick={() => {
                  setSelectedGroup('all');
                  setSelectedTeam('all');
                  setSelectedMonth('all');
                  setSelectedRound('all');
                  setSelectedLeg('all');
                }}
                style={styles.resetButton}
              >
                {language === 'ar' ? '🔄 إعادة تعيين الفلاتر' : '🔄 Réinitialiser les filtres'}
              </button>
            </div>
          )}

          {/* Results Count */}
          <div style={styles.resultsCount}>
            {language === 'ar'
              ? `${filteredMatches.length} مباراة`
              : `${filteredMatches.length} match(s) trouvé(s)`}
          </div>
        </div>
      </section>

      {/* Matches by Date */}
      <section style={styles.matchesSection}>
        <div style={styles.matchesContainer}>
          {sortedDates.length === 0 ? (
            <div style={styles.emptyState}>
              <FaCalendarAlt style={styles.emptyIcon} />
              <p style={styles.emptyText}>
                {language === 'ar' ? 'لا توجد مباريات مجدولة' : 'Aucun match programmé'}
              </p>
            </div>
          ) : (
            sortedDates.map(date => (
              <div key={date} style={styles.dateSection}>
                <div style={styles.dateHeader}>
                  <FaCalendarAlt style={styles.dateIcon} />
                  <h3 style={styles.dateTitle}>{date}</h3>
                  <span style={styles.matchCount}>
                    {matchesByDate[date].length} {language === 'ar' ? 'مباراة' : 'match(s)'}
                  </span>
                </div>

                <div style={styles.matchesTable}>
                  {matchesByDate[date].map(match => {
                    const status = getStatusBadge(match);
                    return (
                      <div key={match.id} style={styles.matchRow}>
                        <div style={styles.matchTime}>
                          <FaClock style={styles.timeIcon} />
                          <span>{new Date(match.match_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>

                        <div style={styles.matchTeams}>
                          <div style={styles.teamHome}>
                            {getLogoUrl(match.home_team_logo) && (
                              <img
                                src={getLogoUrl(match.home_team_logo)}
                                alt={match.home_team_name}
                                style={styles.teamLogo}
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            )}
                            <span style={styles.teamName}>{match.home_team_name}</span>
                          </div>

                          <div style={styles.matchScore}>
                            {match.status === 'finished' ? (
                              <>
                                <span style={styles.score}>{match.home_score}</span>
                                <span style={styles.scoreSeparator}>-</span>
                                <span style={styles.score}>{match.away_score}</span>
                              </>
                            ) : (
                              <span style={styles.vs}>VS</span>
                            )}
                          </div>

                          <div style={styles.teamAway}>
                            <span style={styles.teamName}>{match.away_team_name}</span>
                            {getLogoUrl(match.away_team_logo) && (
                              <img
                                src={getLogoUrl(match.away_team_logo)}
                                alt={match.away_team_name}
                                style={styles.teamLogo}
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            )}
                          </div>
                        </div>

                        <div style={styles.matchInfo}>
                          {match.venue && (
                            <div style={styles.venue}>
                              <FaMapMarkerAlt style={styles.venueIcon} />
                              <span>{match.venue}</span>
                            </div>
                          )}
                          <div style={{ ...styles.statusBadge, background: status.color }}>
                            {status.text}
                          </div>
                          {match.round && (
                            <div style={styles.round}>
                              {language === 'ar' ? `الجولة ${match.round}` : `J${match.round}`}
                              <span style={{ marginLeft: '5px', fontSize: '10px', opacity: 0.8 }}>
                                ({match.leg === 'retour' ? (language === 'ar' ? 'إياب' : 'Retour') : (language === 'ar' ? 'ذهاب' : 'Aller')})
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* PDF Reference Section */}
      <section style={styles.pdfSection}>
        <div style={styles.pdfContainer}>
          <button
            onClick={() => setShowPDF(!showPDF)}
            style={styles.pdfToggle}
          >
            <FaDownload style={styles.pdfIcon} />
            {showPDF
              ? (language === 'ar' ? 'إخفاء الرزنامة الأصلية (PDF)' : 'Masquer le calendrier original (PDF)')
              : (language === 'ar' ? 'عرض الرزنامة الأصلية (PDF)' : 'Afficher le calendrier original (PDF)')}
          </button>

          {showPDF && (
            <div style={styles.pdfContent}>
              <div style={styles.pdfButtons}>
                {groups.map(group => (
                  <div key={group.id} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <button
                      onClick={() => setActivePdfUrl(activePdfUrl === group.file ? null : group.file)}
                      style={{
                        ...styles.pdfDownloadBtn,
                        background: activePdfUrl === group.file ? '#1a365d' : '#f5f5f5',
                        color: activePdfUrl === group.file ? 'white' : '#333',
                        borderColor: activePdfUrl === group.file ? '#1a365d' : '#e0e0e0',
                      }}
                    >
                      {language === 'ar' ? '👀 ' + group.nameAr : '👀 ' + group.name}
                    </button>
                    <a
                      href={group.file}
                      download
                      style={{...styles.pdfDownloadBtn, fontSize: '12px', padding: '5px' }}
                    >
                      {language === 'ar' ? '📥 تحميل' : '📥 Télécharger'}
                    </a>
                  </div>
                ))}
              </div>

              {activePdfUrl && (
                <div style={styles.pdfPreviewContainer}>
                  <div style={styles.pdfPreviewHeader}>
                    <h4 style={{ margin: 0 }}>
                      {language === 'ar' ? 'معاينة الرزنامة' : 'Aperçu du Calendrier'}
                    </h4>
                    <button 
                      onClick={() => setActivePdfUrl(null)}
                      style={styles.closePreviewBtn}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <iframe
                    src={`${activePdfUrl}#toolbar=0`}
                    width="100%"
                    height="600px"
                    style={{ border: 'none', borderRadius: '0 0 10px 10px' }}
                    title="PDF Preview"
                  />
                </div>
              )}
              <p style={styles.pdfNote}>
                {language === 'ar'
                  ? '💡 الرزنامة أعلاه يتم تحديثها تلقائياً من قبل الإدارة. يمكن تغيير التواريخ حسب الظروف الجوية أو أسباب أخرى.'
                  : '💡 Le calendrier ci-dessus est mis à jour automatiquement par l\'administration. Les dates peuvent être modifiées selon les conditions météorologiques ou autres raisons.'}
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: { minHeight: '100vh', background: '#f5f5f5' },
  header: {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.52) 0%, rgba(44, 82, 130, 0.48) 100%), url(${COVER_BG_IMAGE})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '80px 20px',
    textAlign: 'center',
    color: '#fff'
  },
  headerIcon: { fontSize: '80px', marginBottom: '20px' },
  title: { fontSize: '48px', fontWeight: 'bold', marginBottom: '15px', textShadow: '2px 2px 4px rgba(0,0,0,0.3)', color: '#fff' },
  subtitle: { fontSize: '20px', opacity: 0.95, color: '#fff' },
  filterSection: { maxWidth: '1200px', margin: '40px auto', padding: '0 20px' },
  filterContainer: { background: 'white', borderRadius: '15px', padding: '30px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' },
  filterTitle: { fontSize: '24px', fontWeight: 'bold', marginBottom: '25px', color: '#333', textAlign: 'center' },
  filterRow: { marginBottom: '25px' },
  filtersGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '20px' },
  filterItem: { display: 'flex', flexDirection: 'column' },
  filterLabel: { display: 'block', fontSize: '15px', fontWeight: 'bold', color: '#555', marginBottom: '8px' },
  groupButtons: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' },
  groupButton: { padding: '12px 20px', background: '#f5f5f5', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.3s', color: '#333' },
  groupButtonActive: { background: '#1a365d', color: '#fff', borderColor: '#1a365d', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(26, 54, 93, 0.4)' },
  selectFilter: { width: '100%', padding: '12px 15px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '15px', fontWeight: '500', color: '#333', background: 'white', cursor: 'pointer', transition: 'all 0.3s' },
  resetButtonContainer: { marginTop: '20px', textAlign: 'center' },
  resetButton: { padding: '12px 30px', background: '#ff6b6b', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.3s' },
  resultsCount: { marginTop: '20px', padding: '15px', background: '#e3f2fd', borderRadius: '8px', textAlign: 'center', fontSize: '16px', fontWeight: 'bold', color: '#1565c0' },
  matchesSection: { maxWidth: '1200px', margin: '40px auto', padding: '0 20px' },
  matchesContainer: { display: 'flex', flexDirection: 'column', gap: '30px' },
  dateSection: { background: 'white', borderRadius: '15px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' },
  dateHeader: {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.58) 0%, rgba(44, 82, 130, 0.55) 100%), url(${COVER_BG_IMAGE})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '20px 30px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    color: '#fff'
  },
  dateIcon: { fontSize: '24px' },
  dateTitle: { fontSize: '24px', fontWeight: 'bold', margin: 0, flex: 1 },
  matchCount: { fontSize: '14px', background: 'rgba(255,255,255,0.2)', padding: '5px 15px', borderRadius: '20px' },
  matchesTable: { padding: '20px' },
  matchRow: { display: 'grid', gridTemplateColumns: '100px 1fr 200px', gap: '20px', padding: '20px', background: '#f9f9f9', borderRadius: '10px', marginBottom: '15px', alignItems: 'center', transition: 'transform 0.3s, box-shadow 0.3s' },
  matchTime: { display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px', fontWeight: 'bold' },
  timeIcon: { color: '#1a365d' },
  matchTeams: { display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '20px', alignItems: 'center' },
  teamHome: { display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' },
  teamAway: { display: 'flex', alignItems: 'center', gap: '10px' },
  teamLogo: { width: '40px', height: '40px', objectFit: 'contain' },
  teamName: { fontSize: '16px', fontWeight: 'bold', color: '#333' },
  matchScore: { display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', minWidth: '80px' },
  score: { fontSize: '24px', fontWeight: 'bold', color: '#1a365d' },
  scoreSeparator: { fontSize: '20px', color: '#999' },
  vs: { fontSize: '16px', fontWeight: 'bold', color: '#999' },
  matchInfo: { display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' },
  venue: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#666' },
  venueIcon: { fontSize: '12px' },
  statusBadge: { padding: '5px 15px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', color: 'white' },
  round: { fontSize: '12px', color: '#999', fontWeight: 'bold' },
  emptyState: { textAlign: 'center', padding: '80px 20px', color: '#999' },
  emptyIcon: { fontSize: '80px', marginBottom: '20px', opacity: 0.3 },
  emptyText: { fontSize: '18px' },
  pdfSection: { maxWidth: '1200px', margin: '40px auto 60px', padding: '0 20px' },
  pdfContainer: { background: 'white', borderRadius: '15px', padding: '30px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' },
  pdfToggle: { width: '100%', padding: '15px', background: '#1a365d', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', transition: 'background 0.3s' },
  pdfIcon: { fontSize: '20px' },
  pdfContent: { marginTop: '20px' },
  pdfButtons: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '20px' },
  pdfDownloadBtn: { padding: '12px 20px', background: '#f5f5f5', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', textDecoration: 'none', color: '#333', transition: 'all 0.3s' },
  pdfNote: { padding: '15px', background: '#e3f2fd', borderRadius: '8px', color: '#1565c0', fontSize: '14px', textAlign: 'center', margin: 0 },
  pdfPreviewContainer: { marginTop: '20px', border: '2px solid #1a365d', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' },
  pdfPreviewHeader: { background: '#1a365d', color: 'white', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closePreviewBtn: { background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center' }
};


// Styles hover
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  .matchRow:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  }
  button:hover, a:hover {
    transform: translateY(-2px);
  }
  .pdfToggle:hover {
    background: #2c5282 !important;
  }
  .pdfDownloadBtn:hover {
    background: #1a365d !important;
    color: white !important;
    border-color: #1a365d !important;
  }
  select:hover, select:focus {
    border-color: #1a365d !important;
    outline: none;
  }
  .resetButton:hover {
    background: #ff5252 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.4);
  }
`;
if (!document.getElementById('calendar-page-styles')) {
  styleSheet.id = 'calendar-page-styles';
  document.head.appendChild(styleSheet);
}

export default CalendarPage;
